import type { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import type { BetSelection, OddsPreference } from "@/features/betslip/types";

export type BetslipStoreHandle = typeof useBetslipStore;

export type TelemetryProfile = {
  email: string;
  firstName: string;
  lastName: string;
};

export type SubmitBetParams = {
  store: BetslipStoreHandle;
  currency: string;
  preference: OddsPreference;
  userId?: string;
  acceptDuplicate?: boolean;
  profile: TelemetryProfile;
};

type BetResponseError = {
  messageTemplate: string;
  message: string;
  propertyPath?: string;
};

type AcceptedData = {
  betId: string;
  entryId: number;
  outcomeIds: string[];
  correlationId: string;
  partialStake?: number;
  /** The price the bet went on at. Can differ from what we sent. */
  odds?: number;
  /** Multi only - each leg's price, echoed with the correlationId we sent. */
  selections?: AcceptedSelection[];
};

export type AcceptedSelection = {
  correlationId: string;
  odds: number;
  outcomeIds: string[];
};

type RejectedData = {
  betId: string;
  outcomeIds: string[];
  correlationId: string;
  alternativeStake?: number;
};

type InterceptedData = {
  betId: string;
  outcomeIds: string[];
  correlationId: string;
  timeoutAt: string;
};

export type AcceptedBetResponse = {
  code: "bet-accepted" | "bet-partial-stake-accepted";
  status: string;
  data: AcceptedData;
  errors: BetResponseError[];
};

export type RejectedBetResponse = {
  code: "bet-rejected";
  status: string;
  data: RejectedData;
  errors: BetResponseError[];
};

export type InterceptedBetResponse = {
  code: "bet-intercepted";
  status: string;
  data: InterceptedData;
  errors: BetResponseError[];
};

export type DelayedBetResponse = {
  code: "bets-delayed";
  status: string;
  data: InterceptedData;
  errors: BetResponseError[];
};

export type BetResponse =
  | AcceptedBetResponse
  | RejectedBetResponse
  | InterceptedBetResponse
  | DelayedBetResponse;

/** A bet the server is holding: under trader review, or a live delay. */
export type HeldBetResponse = InterceptedBetResponse | DelayedBetResponse;

export type HeldKind = "intercepted" | "delayed";

export const HELD_CODE: Record<HeldKind, HeldBetResponse["code"]> = {
  intercepted: "bet-intercepted",
  delayed: "bets-delayed",
};

export type LastSubmitted = {
  outcomeIds: string[];
  /** Absent on snapshots written before single stakes were tracked. */
  singleStakes?: Record<string, number>;
  multiStake: number;
  /** Absent on snapshots written before combo stakes were tracked. */
  combinationStakes?: Record<string, number>;
};

export type AnalyticsContext = {
  isMulti: boolean;
  betType: "SINGLE" | "MULTI";
  combinedOdds: number;
  multiPayoutDollars: number;
  /** Most common event name in the slip — the multi's display title. */
  commonEventTitle: string;
  /** Event name of the first multi leg — the multi's `market` field. */
  firstMultiLegEventName: string;
  startTimeISO: string;
  hoursBeforeStart: number;
  origin: { type: string; id?: string };
  currency: string;
  profile: TelemetryProfile;
  stakedSingles: BetSelection[];
  correlationToOutcome: Record<string, string>;
  comboCorrelationToType: Record<string, string>;
  comboTotalsByType: Record<string, number>;
};
